'use client';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import classnames from 'classnames';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import React, { useState } from 'react';

import FilterGroups from '@/app/[locale]/(default-layout)/program/_components/filter/FilterGroups';
import ProgramFilterSkeleton from '@/app/[locale]/(default-layout)/program/_components/filter/ProgramFilterSkeleton';

import Button from '@/components/button/Button';
import { ButtonStyle } from '@/components/button/ButtonStyle.enum';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import IconButton from '@cineamo/ui/src/button/IconButton';
import { IconClose24 } from '@cineamo/ui/src/icons/ic-close';
import { IconFilter16, IconFilter24 } from '@cineamo/ui/src/icons/ic-filter';
import { IconReset24 } from '@cineamo/ui/src/icons/ic-reset';
import { IconSort16 } from '@cineamo/ui/src/icons/ic-sort';
import { IconSortFilter48 } from '@cineamo/ui/src/icons/ic-sort-filter';
import { IconTick16 } from '@cineamo/ui/src/icons/ic-tick';
import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';

import MobileOrDesktopRenderer from '@cineamo/composables/src/MobileOrDesktopRenderer';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { useProgramFilterContext } from '@/providers/ProgramFilterProvider';
import { daysBetween } from '@/utils/date-helper';

const SortingOptions: React.FC<{ onChange?: () => void }> = ({ onChange }) => {
    const t = useTranslations();
    const { sorting, setSorting } = useProgramFilterContext();
    const { track } = useAnalytics();
    const locale = useLocale();
    const selectedDate = useSearchParams().get('date') || 'all';
    const daysBetweenSelectedDateAndCurrentDate =
        selectedDate === 'all' ? 'all' : daysBetween(new Date(selectedDate), new Date());

    const sortingGroups = [
        ['date-asc', 'date-desc'],
        ['a-z', 'z-a'],
        ['showings-desc', 'showings-asc'],
        ['newest-asc', 'newest-desc']
    ] as const;

    return (
        <div className="flex flex-col h-full divide-y divide-[rgba(var(--on-surface-color-rgb),0.2)]">
            {sortingGroups.map((group, index) => (
                <div
                    key={`SortingGroup-${index}`}
                    className="">
                    {group.map((item) => (
                        <MenuItem key={`SortingItem-${item}`}>
                            <button
                                onClick={() => {
                                    setSorting(item);
                                    track('Program Sorting Changed', {
                                        sorting: item,
                                        locale: locale,
                                        selectedDate: daysBetweenSelectedDateAndCurrentDate
                                    });
                                    onChange?.();
                                }}
                                className={classnames(
                                    'flex flex-row w-full space-x-8 lg:px-16 lg:py-8 py-12',
                                    'leading-24 text-body-14 text-start items-center',
                                    sorting === item && 'font-bold',
                                    'lg:hover:bg-[rgba(var(--on-surface-color-rgb),0.1)]'
                                )}>
                                <span className="w-16 h-16">{sorting === item && <IconTick16 />}</span>
                                <span>{t('program.sorting-key.' + item)}</span>
                            </button>
                        </MenuItem>
                    ))}
                </div>
            ))}
        </div>
    );
};

const DesktopSortingMenu: React.FC = () => {
    const t = useTranslations();
    return (
        <Menu as="div">
            <MenuButton className="flex flex-row h-48 px-12 items-center space-x-8 text-body-bold-14 bg-transparent text-[var(--on-background-color)]">
                <IconSort16 />
                <span>{t('program.sorting')}</span>
            </MenuButton>
            <MenuItems
                anchor="bottom end"
                transition
                modal={false}
                className={classnames(
                    // The anchored panel is positioned as `fixed`; without a z-index of its own it
                    // would stack at `auto` and every positioned element further down the page -
                    // the program list's sticky date header band, for one - would cover it.
                    'h-auto rounded-8 drop-shadow-[0_35px_35px_rgba(0,0,0,0.20)] z-dropdown',
                    'transition duration-200 ease-out data-[closed]:scale-95 origin-top-right data-[closed]:opacity-0',
                    'bg-[var(--background-color)] text-[var(--on-surface-color)]'
                )}>
                <div className="bg-[var(--surface-color)]">
                    <SortingOptions />
                </div>
            </MenuItems>
        </Menu>
    );
};

const DesktopFilterMenu: React.FC = () => {
    const t = useTranslations();
    const { activeFiltersCount, resetFilters } = useProgramFilterContext();
    return (
        <Menu as="div">
            <MenuButton className="flex flex-row h-48 px-12 items-center space-x-8 text-body-bold-14 bg-transparent text-[var(--on-background-color)]">
                <IconFilter16 />
                <DotSeparatedLabel
                    labels={[
                        t('program.filters'),
                        <span
                            key={1}
                            className="text-[var(--primary-color)]">
                            {activeFiltersCount.toString()}
                        </span>
                    ]}
                />
            </MenuButton>
            <MenuItems
                anchor="bottom end"
                transition
                modal={false}
                className={classnames(
                    // Needs its own z-index for the same reason as the sorting menu above.
                    'w-[400px] h-auto rounded-8 drop-shadow-[0_35px_35px_rgba(0,0,0,0.20)] z-dropdown',
                    'flex',
                    'transition duration-200 ease-out data-[closed]:scale-95 origin-top-right data-[closed]:opacity-0',
                    'bg-[var(--background-color)] text-[var(--on-surface-color)]'
                )}>
                <div className="w-full flex flex-col space-y-16 p-16 h-full bg-[var(--surface-color)]">
                    <div className="flex space-x-16 items-center">
                        <IconFilter24 />
                        <div className="w-0 flex-auto flex flex-col">
                            <span className="text-body-bold-16">{t('program.filters')}</span>
                            <span className="text-body-bold-12 opacity-60">
                                {t('program.n-active-filters', {
                                    count: activeFiltersCount
                                })}
                            </span>
                        </div>
                        <IconButton
                            icon={IconReset24}
                            classNameButton="clickable-element"
                            onClick={resetFilters}
                            accessibilityLabel="Reset filters"
                        />
                    </div>

                    <div className="w-full h-[1px] flex-none bg-[rgba(var(--on-surface-color-rgb),0.5)]" />

                    <FilterGroups classNameFilterItem="h-[28px]" />
                </div>
            </MenuItems>
        </Menu>
    );
};

const MobileSortingMenu: React.FC = () => {
    const t = useTranslations();
    return (
        <MenuItem>
            {({ close }) => (
                <div className="flex flex-col p-16">
                    <div className="text-body-bold-14">{t('program.movie-sorting')}</div>
                    <SortingOptions onChange={() => close()} />
                </div>
            )}
        </MenuItem>
    );
};

const MobileFilterMenu: React.FC = () => {
    const t = useTranslations();
    const { resetFilters } = useProgramFilterContext();
    return (
        <div className="flex flex-col space-y-24 p-16">
            <FilterGroups classNameFilterItem="h-[32px]" />
            <Button
                style={ButtonStyle.text_only}
                leadingIcon={<IconReset24 />}
                onClick={resetFilters}>
                {t('program.reset-filters')}
            </Button>
        </div>
    );
};

const ProgramFilter: React.FC = () => {
    const mounted = useIsMounted();
    const { activeFiltersCount } = useProgramFilterContext();
    const [selectedTab, setSelectedTab] = useState<'filters' | 'sorting'>('filters');
    const t = useTranslations();

    if (!mounted) {
        return <ProgramFilterSkeleton className="hidden lg:flex" />;
    }
    return (
        <MobileOrDesktopRenderer
            desktopContent={
                <div className="flex flex-row space-x-16 relative justify-end">
                    <DesktopSortingMenu />
                    <DesktopFilterMenu />
                </div>
            }
            mobileContent={
                <Menu>
                    <MenuButton
                        className={classnames(
                            'fixed bottom-[72px] right-[0px] z-[500]',
                            'w-[56px] h-[56px] m-[12px]',
                            'flex items-center justify-center space-y-0 rounded-4',
                            'text-body-bold-14 backdrop-blur',
                            'bg-[rgba(var(--on-background-color-rgb),0.1)]'
                        )}
                        aria-label="filter">
                        <div className="flex flex-col space-y-[2px]">
                            <IconSortFilter48 className="w-[32px] h-[32px]" />
                            {activeFiltersCount > 0 && (
                                <span className="text-body-bold-12 leading-[12px] text-[var(--primary-color)]">
                                    {activeFiltersCount}
                                </span>
                            )}
                        </div>
                    </MenuButton>
                    <MenuItems
                        transition
                        className={classnames(
                            'w-full max-h-[100dvh] fixed !left-0 !bottom-0 drop-shadow-[0_35px_35px_rgba(0,0,0,0.20)] z-[800]',
                            'rounded-t-[16px] overflow-hidden',
                            'transition duration-200 ease-out data-[closed]:translate-y-full origin-bottom data-[closed]:opacity-0',
                            'bg-[var(--background-color)]'
                        )}>
                        <div className="flex flex-col h-[100dvh] max-h-[100dvh]">
                            <MenuItem>
                                {({ close }) => (
                                    <div className="flex flex-row justify-end mt-[12px] mb-[10px] flex-shrink-0">
                                        <IconButton
                                            icon={IconClose24}
                                            classNameButton="text-[var(--on-background-color)] px-[16px] box-content"
                                            onClick={() => close()}
                                            accessibilityLabel="Close filter"
                                        />
                                    </div>
                                )}
                            </MenuItem>
                            <div className="flex flex-row px-16 flex-shrink-0">
                                <button
                                    className={classnames(
                                        selectedTab === 'filters'
                                            ? 'text-[var(--on-surface-color)] bg-[var(--surface-color)]'
                                            : 'text-[rgba(var(--on-background-color-rgb),0.7)] bg-transparent',
                                        'w-1/2 rounded-t-[12px] px-[9px] py-10 flex flex-row space-x-8 items-center justify-center text-body-12'
                                    )}
                                    onClick={() => setSelectedTab('filters')}>
                                    <span>
                                        <IconFilter16 className="w-[20px] h-[20px]" />
                                    </span>
                                    <DotSeparatedLabel
                                        labels={[
                                            t('program.filters'),
                                            <span
                                                key={1}
                                                className="text-[var(--primary-color)]">
                                                {activeFiltersCount.toString()}
                                            </span>
                                        ]}
                                    />
                                </button>
                                <button
                                    className={classnames(
                                        selectedTab === 'sorting'
                                            ? 'text-[var(--on-surface-color)] bg-[var(--surface-color)]'
                                            : 'text-[rgba(var(--on-background-color-rgb),0.7)] bg-transparent',
                                        'w-1/2 rounded-t-[12px] px-[9px] py-10 flex flex-row space-x-8 items-center justify-center text-body-12'
                                    )}
                                    onClick={() => setSelectedTab('sorting')}>
                                    <span>
                                        <IconSort16 className="w-[20px] h-[20px]" />
                                    </span>
                                    <span>{t('program.sorting')}</span>
                                </button>
                            </div>
                            <div className="bg-[var(--surface-color)] rounded-t-[12px] flex-1 overflow-y-auto">
                                {selectedTab === 'filters' ? <MobileFilterMenu /> : <MobileSortingMenu />}
                            </div>
                        </div>
                    </MenuItems>
                </Menu>
            }
        />
    );
};

export default ProgramFilter;
